/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';

import { validation } from '@ibm-watson/shared/modules/ia/util/validation';
import { ASC, DESC, NONE } from './constants';
import { PhoneImportModalMessages as messages } from './messages';

export const normalizePhoneNumber = phoneNum => {
  // Remove all Non Number characters, ensure plus at beginning
  // If it is a UUID we tread it as ''.

  // if the string begins with '(' replace that with '1'
  // Note that Twilio/providers present the format "(nnn)nnn-nnnn" prominently
  //  so we expect it easy for our user to copy/paste that
  if (phoneNum && phoneNum.startsWith('(')) {
    phoneNum = phoneNum.replace('(', '1');
  }
  return phoneNum && phoneNum !== ''
    ? `+${phoneNum.replace(/\D/g, '').trim()}`
    : '';
};

const parseCsvColumn = text => {
  // remove trailing \r \n if present
  const trim1 = text.replace('\n', '');
  const trim2 = trim1.replace('\r', '');
  let colVal = trim2,
    rest = '';
  if (trim2.length) {
    const chars = trim2.split('');
    const eol = trim2.length;
    const parsedChars = [];
    // quoteActive tracks whether we're in a quoted field: ...,"here, there", => 'here, there'
    // note also that back-to-back "" is escaped quote:
    //  ..., "he said, ""yo""", => 'he said, "yo"'
    let quoteActive = false;
    let done = false;
    let index = 0;

    while (index < eol && !done) {
      const currChar = chars[index];
      if (currChar === '"') {
        if (index + 1 < eol && chars[index + 1] === '"') {
          //escaped quote
          parsedChars.push('"');
          index = index + 2;
        } else {
          // no push to parsedChars, flip quoteActive
          quoteActive = !quoteActive;
          index++;
        }
      } else if (currChar === ',' && !quoteActive) {
        done = true;
        index++;
      } else {
        parsedChars.push(currChar);
        index++;
      }
    }
    colVal = parsedChars.join('');
    colVal = colVal.trim();
    if (index < eol) {
      rest = trim2.substring(index);
    }
  }
  return [colVal, rest];
};

//
// parsePhoneCSV is a separate export for easier unit testing
///
/**
 * Parses the input string as a CSV file, specifically capturing only
 * the first two fields of a line as phone, description
 * Missing or malformed phone throws an error with line number
 * Missing description is set as '' in output array
 * @param {string} text Input CSV file content as a string
 * @returns {Array} An array of phone objects (id, name, phone_number, primary)
 */
export const parsePhoneCSV = (text, currCount) => {
  const retVal = [];
  let phoneId = currCount;
  if (text && text.length) {
    const lines = text.split(/\r?\n/);
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      let phone = '',
        desc = '';
      const line = lines[lineIndex];
      if (line.length > 0) {
        const firstCol = line.indexOf(',');
        if (firstCol === -1) {
          // no description
          phone = line;
        } else {
          phone = line.substring(0, firstCol);
          const rest = line.substring(firstCol + 1);
          const next = parseCsvColumn(rest);
          desc = next[0];
        }
        if (phone.length === 0) {
          // if empty phone, throw error
          const msg = messages.importPhoneError.defaultMessage
            .replaceAll('{lineNo}', `${lineIndex + 1}`)
            .replaceAll('{errMsg}', messages.emptyPhoneError.defaultMessage);
          throw new SyntaxError(msg);
        }
        const preNormErr = validation.validatePhoneNumber({
          phone_number: phone
        });
        if (preNormErr) {
          const msg = messages.importPhoneError.defaultMessage
            .replaceAll('{lineNo}', `${lineIndex + 1}`)
            .replaceAll('{errMsg}', `${preNormErr.phone_number[0]}`);
          throw new SyntaxError(msg);
        }
        const normPhone = normalizePhoneNumber(phone);
        const normError = validation.validateNormalizedPhone({
          phone_number: normPhone
        });
        if (normError) {
          const msg = messages.importPhoneError.defaultMessage
            .replaceAll('{lineNo}', `${lineIndex + 1}`)
            .replaceAll('{errMsg}', `${normError.phone_number[0]}`);
          throw new SyntaxError(msg);
        }
        const descError = validation.validatePhoneDescription({
          description: desc
        });
        if (descError) {
          const msg = messages.importPhoneError.defaultMessage
            .replaceAll('{lineNo}', `${lineIndex + 1}`)
            .replaceAll('{errMsg}', `${descError.description[0]}`);
          throw new SyntaxError(msg);
        }
        const entry: any = { primary: false };
        entry.phone_number = normPhone;
        entry.name = desc;
        entry.id = phoneId.toString();
        retVal.push(entry);
        phoneId++;
      }
    }
  }
  return retVal;
};

export const getSortedData = (arrayIn, sortColName, dir) => {
  if (dir === NONE) {
    return _.orderBy(
      arrayIn,
      [
        function (o) {
          return parseInt(o.id);
        }
      ],
      [ASC]
    );
  } else if (sortColName === 'primary') {
    // want asc on status col to put the primary number first, so flip as orderBy puts false ahead of true
    const primDir = dir === ASC ? DESC : ASC;
    return _.orderBy(arrayIn, ['primary', 'inUseError'], [primDir, dir]);
  } else {
    return _.orderBy(arrayIn, [sortColName], [dir]);
  }
};

export const displayNum = (phoneNumber: string): string => {
  return (
    phoneNumber.substr(0, 2) +
    ' (' +
    phoneNumber.substr(2, 3) +
    ') ' +
    phoneNumber.substr(5, 3) +
    '-' +
    phoneNumber.substr(8, 4)
  );
};
